import { useState, useEffect, useMemo } from 'react';
import { hrms, getFileUrl } from '../../services/api';
import { theme } from '../../theme';

// ── Connector CSS injected once ───────────────────────────────────────────────
// Each .oc-child wrapper draws its half of the horizontal connector bar via
// ::before. Because alignItems:center keeps each card at 50% of wrapper width,
// the bar always hits card centers regardless of how wide expanded subtrees are.
const CONNECTOR_CSS = `
  .oc-row { display: flex; }
  .oc-child {
    display: flex; flex-direction: column; align-items: center;
    padding: 0 16px; position: relative;
  }
  .oc-child::before {
    content: ''; position: absolute; top: 0;
    left: 0; right: 0; height: 2px; background: #D1D5DB;
  }
  .oc-child:first-child::before  { left: 50%; }
  .oc-child:last-child::before   { right: 50%; }
  .oc-child:only-child::before   { display: none; }
`;

function Avatar({ photo, name, size = 52 }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#0EA5E9', '#F59E0B'];
  const color  = COLORS[(name || '').charCodeAt(0) % COLORS.length];
  const url    = photo ? getFileUrl(photo) : null;

  if (url && !failed) {
    return (
      <img src={url} alt={name} onError={() => setFailed(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover',
                 border: '2px solid #fff', flexShrink: 0 }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: size * 0.35, fontWeight: 700, color: '#fff', border: '2px solid #fff' }}>
      {initials}
    </div>
  );
}

const ROLE_COLORS = {
  super_admin:       '#7C3AED',
  hr_admin:          '#0369A1',
  hr_head:           '#0369A1',
  hr_executive:      '#0284C7',
  it_admin:          '#0F766E',
  finance_head:      '#B45309',
  finance_team:      '#D97706',
  cto:               '#1E40AF',
  ceo:               '#9D174D',
  project_manager:   '#0F766E',
  reporting_manager: '#1D4ED8',
  team_lead:         '#4338CA',
  employee:          '#374151',
};
const roleColor = (role) => ROLE_COLORS[role] || '#374151';

// ── Single org card + its subtree ─────────────────────────────────────────────
function OrgNode({ node, level = 0 }) {
  const [expanded, setExpanded]  = useState(level < 1);
  const [hovered, setHovered]    = useState(false);
  const hasChildren = node.children?.length > 0;
  const rc = roleColor(node.role);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

      {/* ── Card ── */}
      <div
        onClick={() => hasChildren && setExpanded(e => !e)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          width: 170,
          background: hovered ? `linear-gradient(160deg,#fff,${rc}0a)` : '#fff',
          border: `2px solid ${hovered ? rc : rc + 'AA'}`,
          borderRadius: 12,
          padding: '0.875rem 0.75rem 0.75rem',
          textAlign: 'center',
          cursor: hasChildren ? 'pointer' : 'default',
          transition: 'box-shadow 0.2s, border-color 0.2s, background 0.2s',
          boxShadow: hovered
            ? `0 0 0 3px ${rc}22, 0 8px 24px ${rc}28`
            : `0 2px 0 ${rc}22, 0 4px 12px rgba(0,0,0,0.07)`,
        }}>

        {/* Avatar + status dot */}
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '0.5rem' }}>
          <Avatar photo={node.profile_photo} name={node.name} size={52} />
          <span style={{ position: 'absolute', bottom: 0, right: 0, width: 13, height: 13,
                         borderRadius: '50%', background: rc, border: '2px solid #fff' }} />
        </div>

        {/* Name */}
        <p style={{ margin: '0 0 0.15rem', fontSize: '0.8rem', fontWeight: 700, color: theme.text,
                    lineHeight: 1.3, wordBreak: 'break-word', hyphens: 'auto' }}>
          {node.name}
        </p>

        {/* Emp code */}
        {node.emp_code && (
          <p style={{ margin: '0 0 0.2rem', fontSize: '0.63rem', fontWeight: 700, color: rc }}>
            #{node.emp_code}
          </p>
        )}

        {/* Designation badge */}
        {node.designation && (
          <span style={{ display: 'inline-block', fontSize: '0.6rem', fontWeight: 700,
                         background: rc, color: '#fff', borderRadius: 9999,
                         padding: '0.12rem 0.45rem', marginBottom: '0.2rem',
                         textTransform: 'uppercase', letterSpacing: '0.03em',
                         maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.designation}
          </span>
        )}

        {/* Department */}
        {node.department && (
          <p style={{ margin: '0.1rem 0 0', fontSize: '0.63rem', color: theme.textMuted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
            {node.department}
          </p>
        )}

        {/* Expand toggle */}
        {hasChildren && (
          <p style={{ margin: '0.3rem 0 0', fontSize: '0.62rem', color: theme.textMuted }}>
            {expanded ? '▲' : '▼'} {node.children.length} report{node.children.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* ── Connectors + children ── */}
      {hasChildren && expanded && (
        <>
          {/* Vertical line down from card */}
          <div style={{ width: 2, height: 22, background: '#D1D5DB', flexShrink: 0 }} />

          {/* Children row — gap:0 so ::before segments join up seamlessly */}
          <div className="oc-row">
            {node.children.map(child => (
              <div key={child.id} className="oc-child">
                {/* Vertical line from horizontal bar down to child card */}
                <div style={{ width: 2, height: 22, background: '#D1D5DB', flexShrink: 0 }} />
                <OrgNode node={child} level={level + 1} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Designation summary ───────────────────────────────────────────────────────
const PALETTE = ['#3B82F6','#10B981','#F97316','#8B5CF6','#EF4444','#0EA5E9','#F59E0B','#EC4899'];

function DesignationTable({ byDesignation, total }) {
  const [open, setOpen] = useState(false);
  const rows = Object.entries(byDesignation).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                 padding: '0.75rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer',
                 borderBottom: open ? `1px solid ${theme.border}` : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: theme.text }}>Designation Summary</span>
          <span style={{ background: '#EEF2FF', color: theme.primary, fontSize: '0.72rem', fontWeight: 700,
                         padding: '0.15rem 0.6rem', borderRadius: 9999 }}>
            {rows.length} roles · {total} employees
          </span>
        </div>
        <span style={{ fontSize: '0.8rem', color: theme.textMuted, fontWeight: 600 }}>{open ? '▲ Collapse' : '▼ Expand'}</span>
      </button>
      {open && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                {['#', 'Designation', 'Count', 'Distribution'].map(h => (
                  <th key={h} style={{ padding: '0.55rem 1rem', textAlign: 'left', fontWeight: 700,
                                       color: theme.textMuted, fontSize: '0.72rem', textTransform: 'uppercase',
                                       letterSpacing: '0.05em', borderBottom: `1px solid ${theme.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([desig, count], i) => {
                const c   = PALETTE[i % PALETTE.length];
                const pct = Math.round((count / total) * 100);
                return (
                  <tr key={desig} style={{ borderBottom: `1px solid ${theme.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '0.55rem 1rem', color: theme.textMuted, fontWeight: 600, fontSize: '0.75rem' }}>{i + 1}</td>
                    <td style={{ padding: '0.55rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: c, flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, color: theme.text }}>{desig}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.55rem 1rem' }}>
                      <span style={{ background: c + '18', color: c, fontWeight: 700, fontSize: '0.83rem',
                                     padding: '0.15rem 0.55rem', borderRadius: 6 }}>{count}</span>
                    </td>
                    <td style={{ padding: '0.55rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ flex: 1, height: 5, background: '#F3F4F6', borderRadius: 99, overflow: 'hidden', maxWidth: 180 }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: c, borderRadius: 99 }} />
                        </div>
                        <span style={{ fontSize: '0.72rem', color: theme.textMuted, minWidth: 28 }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
function OrgChart() {
  const [roots,   setRoots]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [stats,   setStats]   = useState({ total: 0, byDesignation: {} });

  useEffect(() => {
    hrms.getOrgChart().then(r => {
      const data = r.data || [];
      setRoots(data);

      const count = (nodes, acc = { total: 0, byDesignation: {} }) => {
        nodes.forEach(n => {
          acc.total++;
          const d = n.designation || 'Unassigned';
          acc.byDesignation[d] = (acc.byDesignation[d] || 0) + 1;
          if (n.children?.length) count(n.children, acc);
        });
        return acc;
      };
      setStats(count(data));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const allNodes = useMemo(() => {
    const result = [];
    const flatten = nodes => nodes.forEach(n => { result.push(n); if (n.children?.length) flatten(n.children); });
    flatten(roots);
    return result;
  }, [roots]);

  const q        = search.trim().toLowerCase();
  const filtered = q ? allNodes.filter(n =>
    (n.name        || '').toLowerCase().includes(q) ||
    (n.emp_code    || '').toLowerCase().includes(q) ||
    (n.designation || '').toLowerCase().includes(q) ||
    (n.department  || '').toLowerCase().includes(q)
  ) : null;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading org chart…</div>
  );

  return (
    <>
      {/* Inject connector CSS once */}
      <style>{CONNECTOR_CSS}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Organization Chart</h1>
            <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{stats.total} active employees</p>
          </div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                           color: theme.textMuted, pointerEvents: 'none' }}>🔍</span>
            <input type="text" placeholder="Search by name, code, designation…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '0.6rem 2.25rem 0.6rem 2.25rem', border: `1.5px solid ${q ? theme.primary : theme.border}`,
                       borderRadius: 8, fontSize: '0.875rem', width: 290, outline: 'none', color: theme.text }} />
            {search && (
              <button onClick={() => setSearch('')}
                style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1rem', padding: 0 }}>
                ×
              </button>
            )}
          </div>
        </div>

        {/* Designation table */}
        <DesignationTable byDesignation={stats.byDesignation} total={stats.total} />

        {!q && (
          <p style={{ fontSize: '0.78rem', color: theme.textMuted, margin: 0 }}>
            Showing 2 levels by default. Click any card to expand or collapse reports.
          </p>
        )}

        {/* Search results */}
        {q && (
          <div style={{ background: '#fff', border: `1.5px solid ${theme.primary}30`, borderRadius: 10, padding: '1rem 1.25rem' }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 700, color: theme.textMuted, marginBottom: filtered.length ? '0.875rem' : 0 }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
            </p>
            {filtered.length === 0 ? (
              <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: 0 }}>No employees match your search.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {filtered.map(n => (
                  <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem',
                                           padding: '0.625rem 0.75rem', borderRadius: 8,
                                           border: `1px solid ${theme.border}`, background: '#FAFAFA' }}>
                    <Avatar photo={n.profile_photo} name={n.name} size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: theme.text }}>{n.name}</p>
                      <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>
                        {n.emp_code    && <span style={{ fontWeight: 600, color: theme.primary, marginRight: '0.4rem' }}>{n.emp_code}</span>}
                        {n.designation && <span>{n.designation}</span>}
                        {n.department  && <span style={{ marginLeft: '0.4rem' }}>· {n.department}</span>}
                      </p>
                    </div>
                    <span style={{ background: roleColor(n.role), color: '#fff', fontSize: '0.65rem',
                                   fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 9999,
                                   whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {(n.role || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tree */}
        {!q && (
          <div style={{ overflowX: 'auto', paddingBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center',
                          minWidth: 'max-content', padding: '1.25rem 1rem' }}>
              {roots.map(root => <OrgNode key={root.id} node={root} level={0} />)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default OrgChart;
